import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TREE_DEMO } from "@/constants/testIds";

/**
 * Interactive family tree demo — 3 generations, 7 nodes.
 * SVG connectors + node cards. Clicking a node reveals a detail card.
 */

const TREE = {
  gp1: {
    id: "gp1", gen: 0, name: "Bhaskaran Nair", years: "1901 — 1989",
    role: "Great-grandfather", place: "Kalpathy, Palakkad",
    story: "Sanskrit scholar. Kept the tharavad ledgers in Malayalam script and Tamil verse.",
    portrait: "https://images.pexels.com/photos/30320070/pexels-photo-30320070.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  gp2: {
    id: "gp2", gen: 0, name: "Kamalakshi Amma", years: "1908 — 1994",
    role: "Great-grandmother", place: "Kalpathy, Palakkad",
    story: "Grew paddy on twelve acres. Taught nine grandchildren to sing sopana sangeetham.",
    portrait: "https://images.pexels.com/photos/8642297/pexels-photo-8642297.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  p1: {
    id: "p1", gen: 1, name: "Ravi Menon", years: "1938 — 2019",
    role: "Grandfather", place: "Palakkad → Bombay",
    story: "Left Palakkad in 1956 with a suitcase and a violin. Built a life on Marine Drive.",
    portrait: "https://images.unsplash.com/photo-1732303105237-f5b5f7a2390e?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
  },
  p2: {
    id: "p2", gen: 1, name: "Sarada Menon", years: "1944 — living",
    role: "Grandmother", place: "Palakkad → Bombay",
    story: "First woman in her village to attend college. Taught Malayalam literature for 42 years.",
    portrait: "https://images.unsplash.com/photo-1643474003587-8bbf4bbc01d9?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
  },
  c1: {
    id: "c1", gen: 2, name: "Anand Menon", years: "1971 — living",
    role: "Father", place: "Bombay → Bangalore",
    story: "Software engineer. Speaks four languages, mispronounces all of them beautifully.",
    portrait: "https://images.unsplash.com/photo-1732303105237-f5b5f7a2390e?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
  },
  c2: {
    id: "c2", gen: 2, name: "Meera Menon", years: "1974 — living",
    role: "Aunt", place: "Bombay → Toronto",
    story: "Radiologist in Toronto. Sends home mangoes every summer without fail.",
    portrait: "https://images.pexels.com/photos/8642297/pexels-photo-8642297.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  you: {
    id: "you", gen: 3, name: "You", years: "The story continues",
    role: "The next chapter", place: "Wherever you call home",
    story: "Add your name, your parents' names, the village they came from. Every archive begins with one entry.",
    portrait: "https://images.unsplash.com/photo-1719339285288-0790b8867c6e?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
  },
};

// positions (percent of container)
const POS = {
  gp1: { x: 22, y: 15 },
  gp2: { x: 58, y: 15 },
  p1: { x: 30, y: 48 },
  p2: { x: 68, y: 48 },
  c1: { x: 40, y: 80 },
  c2: { x: 78, y: 80 },
  you: { x: 12, y: 80 },
};

const EDGES = [
  ["gp1", "p1"], ["gp2", "p1"],
  ["gp1", "p2"], ["gp2", "p2"],
  ["p1", "c1"], ["p2", "c1"],
  ["p1", "c2"], ["p2", "c2"],
  ["p1", "you"], ["p2", "you"],
];

function Node({ node, active, onClick }) {
  const pos = POS[node.id];
  const isYou = node.id === "you";
  return (
    <motion.button
      onClick={() => onClick(node.id)}
      data-testid={TREE_DEMO.node(node.id)}
      initial={{ opacity: 0, scale: 0.7 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.05 * node.gen }}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.96 }}
      className={`node-hover absolute -translate-x-1/2 -translate-y-1/2 group text-left ${active ? "z-30" : "z-20"}`}
      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
    >
      <div className={`relative w-[112px] sm:w-[128px]`}>
        <div className={`arch overflow-hidden border transition ${active ? "border-saffron shadow-[0_12px_40px_-16px_rgba(232,106,51,0.6)]" : "border-ivory-300"}`}>
          {isYou ? (
            <div className="w-full h-[130px] sm:h-[150px] bg-gradient-to-b from-ivory-100 to-ivory-200 flex items-center justify-center">
              <span className="font-serif text-4xl text-tulsi">+</span>
            </div>
          ) : (
            <img
              src={node.portrait}
              alt={node.name}
              className="w-full h-[130px] sm:h-[150px] object-cover grayscale-[0.2] sepia-[0.15]"
            />
          )}
        </div>
        <div className="mt-2 text-center">
          <div className="font-serif text-sm text-ink leading-tight">{node.name}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-50 mt-0.5">{node.role}</div>
        </div>
        {active && (
          <span className="absolute -inset-1.5 border border-saffron/50 rounded-[999px_999px_16px_16px] pointer-events-none" />
        )}
      </div>
    </motion.button>
  );
}

function Edges({ activeId }) {
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100">
      {EDGES.map(([a, b], i) => {
        const A = POS[a], B = POS[b];
        const isHot = activeId && (a === activeId || b === activeId);
        const midY = (A.y + B.y) / 2;
        const path = `M ${A.x} ${A.y} C ${A.x} ${midY}, ${B.x} ${midY}, ${B.x} ${B.y}`;
        return (
          <motion.path
            key={i}
            d={path}
            fill="none"
            stroke={isHot ? "#E86A33" : "#2D4A3E"}
            strokeOpacity={isHot ? 0.85 : 0.35}
            strokeWidth={isHot ? 0.35 : 0.22}
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, delay: i * 0.08, ease: "easeInOut" }}
          />
        );
      })}
    </svg>
  );
}

export default function TreeDemo() {
  const [active, setActive] = useState("gp1");
  const node = TREE[active];

  return (
    <section
      id="tree-demo"
      data-testid={TREE_DEMO.root}
      className="relative py-28 md:py-40 bg-ivory-100/60 border-y border-ivory-300"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-7">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">Try the builder</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[0.95]">
              A tree you can
              <span className="italic text-tulsi"> touch. </span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-ink-50 text-base md:text-lg font-light leading-relaxed">
              Tap any portrait. Watch the connections light up. This is a taste — the real tree
              can hold ten generations, adopted lines, step-families and every branch of your kin.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-12 gap-8">
          {/* Tree canvas */}
          <div className="col-span-12 lg:col-span-8">
            <div className="relative aspect-[3/2] w-full rounded-2xl bg-ivory border border-ivory-300 overflow-hidden">
              {/* faint concentric arcs */}
              <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <pattern id="dot" width="4" height="4" patternUnits="userSpaceOnUse">
                    <circle cx="1" cy="1" r="0.35" fill="#2D4A3E" opacity="0.25" />
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#dot)" />
              </svg>
              <Edges activeId={active} />
              {Object.values(TREE).map((n) => (
                <Node key={n.id} node={n} active={active === n.id} onClick={setActive} />
              ))}
              {/* Generation labels */}
              <div className="absolute left-4 top-4 text-[10px] tracking-[0.28em] uppercase text-ink-50">Gen · I</div>
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[10px] tracking-[0.28em] uppercase text-ink-50">Gen · II</div>
              <div className="absolute left-4 bottom-4 text-[10px] tracking-[0.28em] uppercase text-ink-50">Gen · III</div>
            </div>
          </div>

          {/* Detail card */}
          <div className="col-span-12 lg:col-span-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                data-testid={TREE_DEMO.detail}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="border border-ivory-300 rounded-2xl p-8 bg-ivory sticky top-32"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 arch overflow-hidden border border-ivory-300">
                    {node.id === "you" ? (
                      <div className="w-full h-full bg-tulsi text-ivory flex items-center justify-center font-serif text-2xl">+</div>
                    ) : (
                      <img src={node.portrait} alt="" className="w-full h-full object-cover grayscale-[0.15] sepia-[0.15]" />
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-saffron">{node.role}</div>
                    <div className="font-serif text-2xl text-ink leading-tight">{node.name}</div>
                    <div className="text-xs text-ink-50 mt-0.5">{node.years}</div>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-ink-50 min-w-[70px]">Home</span>
                    <span className="text-ink">{node.place}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-ink-50 min-w-[70px]">Story</span>
                    <span className="text-ink font-light leading-relaxed">{node.story}</span>
                  </div>
                </div>

                <a
                  href="#top"
                  onClick={(e) => { e.preventDefault(); document.getElementById("final-cta")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="mt-6 inline-flex items-center gap-2 text-sm text-tulsi font-medium ink-link"
                >
                  Add this person to your archive →
                </a>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
